import { XMarkIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import avatar from "../../assets/images/Avatar.avif";
import SectionLoader from "../sharedComponents/Loading/SectionLoader";

const PostDetailsModal = ({ isOpen, onClose, post }) => {
    const { t } = useTranslation();

    const getTypeBadge = () => {
        if (post?.type === "admin") {
            return (
                <span className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-xl">
                    {t("postDetails.admin")}
                </span>
            );
        }

        if (post?.type === "company") {
            return (
                <span className="px-3 py-1 text-xs font-medium text-purple-600 bg-purple-100 rounded-xl">
                    {t("postDetails.company")}
                </span>
            );
        }

        return null;
    };

    return (
        <div
            className={`fixed inset-0 z-[999] flex items-center justify-center transition-all duration-300 ${isOpen ? "visible" : "invisible"
                }`}
        >
            {/* Overlay */}
            <div
                onClick={onClose}
                className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"
                    }`}
            />

            {/* Modal */}
            <div
                className={`relative z-10 w-full max-w-lg mx-4 p-5 rounded-2xl bg-white dark:bg-bg-dark-secondary shadow-xl transform transition-all duration-300 ${isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"
                    }`}
            >
                {!post ? (
                    <SectionLoader />
                ) : (
                    <>
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-black dark:text-white">
                                {t("postDetails.details")}
                            </h2>
                            <button onClick={onClose}>
                                <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-black dark:hover:text-white" />
                            </button>
                        </div>

                        {/* User Info */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <img
                                    src={post?.isCompany ? post?.Company?.logo : post?.User?.avatar || avatar}
                                    className="w-10 h-10 rounded-full"
                                    alt={post?.User?.fullName}
                                />
                                <div>
                                    <p className="font-semibold text-black dark:text-white">
                                        {post?.isCompany ? post?.Company?.name_EN : post?.User?.fullName}
                                    </p>
                                    {!post?.isCompany &&
                                        <p className="text-sm text-gray-500">
                                            @{!post?.isCompany && post?.User?.code}
                                        </p>
                                    }
                                </div>
                            </div>

                            {getTypeBadge()}
                        </div>

                        {/* Status Badges */}
                        <div className="flex flex-wrap gap-2 mb-4">
                            {post?.isActive ? (
                                <span className="px-3 py-1 text-xs text-green-600 bg-green-100 rounded-xl">
                                    {t("postDetails.active")}
                                </span>
                            ) : (
                                <span className="px-3 py-1 text-xs text-red-600 bg-red-100 rounded-xl">
                                    {t("postDetails.inactive")}
                                </span>
                            )}

                            {post?.isTrending && (
                                <span className="px-3 py-1 text-xs text-orange-600 bg-orange-100 rounded-xl">
                                    🔥 {t("postDetails.trending")}
                                </span>
                            )}

                            {post?.isSponsored && (
                                <span className="px-3 py-1 text-xs text-teal-600 bg-teal-100 rounded-xl">
                                    💎 {t("postDetails.sponsored")}
                                </span>
                            )}

                            {post?.isAds && (
                                <span className="px-3 py-1 text-xs text-pink-600 bg-pink-100 rounded-xl">
                                    {t("postDetails.ads")}
                                </span>
                            )}
                        </div>

                        {/* Image */}
                        {post?.image && (
                            <img
                                src={post?.image}
                                alt="post"
                                className="object-cover w-full h-64 mb-4 rounded-lg"
                            />
                        )}

                        {/* Text */}
                        <p className="mb-4 text-gray-800 dark:text-gray-200">
                            {post?.text}
                        </p>

                        {/* URL */}
                        {post?.url && (
                            <a
                                href={post?.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block mb-4 text-sm text-blue-600 underline break-all"
                            >
                                🔗 {post?.url}
                            </a>
                        )}

                        {/* Stats */}
                        {/* <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-300">
                            <span>{t("postDetails.likes")}: {post?.likesCount}</span>
                            <span>{t("postDetails.comments")}: {post?.commentsCount}</span>
                            <span>{t("postDetails.shares")}: {post?.sharesCount}</span>
                            <span>{t("postDetails.views")}: {post?.viewsCount}</span>
                        </div> */}

                        {/* Date */}
                        <div className="mt-4 text-xs text-gray-400">
                            {t("postDetails.createdAt")}:{" "}
                            {new Date(post?.createdAt).toLocaleString()}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PostDetailsModal;
